// ViewerController — the renderer-swap seam.
//
// Public surface for the viewer. The Three.js implementation lives behind this
// interface in three-renderer.ts; swapping to Babylon means writing a sibling
// implementation, no caller changes required.
//
// The explode *behaviour* is a second, independent seam: the renderer delegates
// "how far does each part move" to an ExplodeStrategy (see
// ./explode/explode-strategy.ts). v0 ships RadialHierarchyExplode (geometric,
// no solver); the v1 manifest `explosion-graph` (idiagram#3) plugs in as a
// different strategy without touching this interface, the renderer's animation
// loop, the slider, or the overlay.

import type { ExplodeStrategy, ExplosionGraphNode } from './explode/explode-strategy.js';

export interface AssemblyManifestErp {
  provider: 'odoo' | 'csv';
  rootProductRef?: string;
  nodes: Array<{ idiagramId: string; productRef: string }>;
}

/** The thin explosion-graph the converter emits (idiagram#3). Optional —
 *  present only when the converter could solve one (skipped on
 *  empty-tessellation / fully-flattened sources). When present, the viewer
 *  defaults to ExplosionGraphExplode; otherwise it uses RadialHierarchyExplode. */
export interface AssemblyManifestExplosionGraph {
  /** Schema version of the explosion-graph payload (see docs/explosion-graph-schema.md). */
  version: number;
  /** Solver that produced it, e.g. 'li2008-greedy-mesh'. */
  solver: string;
  nodes: ExplosionGraphNode[];
}

export interface AssemblyManifest {
  schemaVersion: '2';
  generator: { name: string; version: string };
  source: { name: string; sha256: string; importedAt: string };
  kernel: { name: string; linearDeflection: number; angularDeflection: number };
  geometry: {
    nodeCount: number;
    meshCount: number;
    triangleCount: number;
    units: 'meter';
    axisConvention: 'Y-up';
    sourceBbox: { min: [number, number, number]; max: [number, number, number] };
  };
  files: { model: string };
  /** Optional. Present when the converter was run with --erp-map. */
  erp?: AssemblyManifestErp;
  /** Optional. Present when the converter computed a blocking-aware explosion
   *  graph (idiagram#3). Drives ExplosionGraphExplode. */
  explosionGraph?: AssemblyManifestExplosionGraph;
}

export interface LoadedNode {
  /** idiagram id from glTF node.extras. */
  id: string;
  /** Original CAD name. */
  name: string;
  /** Child idiagram ids. */
  childIds: string[];
  /** Parent idiagram id, or null at the root. */
  parentId: string | null;
  /** True for synthetic mesh-wrapper nodes inserted by the converter when a
   *  CAD node has multiple meshes. The tree panel should hide these. */
  synthetic: boolean;
}

/** A single per-frame anchor for one node's centroid, in canvas-relative
 *  pixel coordinates. `visible` is false when the point is behind the camera
 *  or outside the frustum's near/far planes. */
export interface Anchor {
  id: string;
  x: number;
  y: number;
  visible: boolean;
}

/** Snapshot of all anchors for one rendered frame, plus the canvas size the
 *  pixel coordinates are relative to. Subscribers get one of these per frame. */
export interface AnchorFrame {
  width: number;
  height: number;
  anchors: Anchor[];
}

export interface ViewerController {
  loadAssembly(manifestUrl: string): Promise<void>;
  getManifest(): AssemblyManifest | null;
  getNodes(): Map<string, LoadedNode>;
  getRootId(): string | null;
  fitView(): void;
  /** Replace the current selection with the given node ids and apply highlight. */
  selectNodes(ids: string[]): void;
  /** Clear all highlights and lift any isolate dimming. */
  clearSelection(): void;
  /** Smoothly fly the camera so the given node ids fill the view, framing
   *  their union bounding sphere along a sensible axis with an eased curve.
   *  Pass an empty list to ease back to the whole-assembly fit. */
  frameNodes(ids: string[]): void;
  /** Visually isolate the given node ids: fade every other part down so the
   *  target reads clearly (the "expose target" half of fit-to-target). Pass an
   *  empty list to restore full opacity. Independent of selectNodes so a host
   *  can highlight without dimming, or vice versa. */
  isolateNodes(ids: string[]): void;
  /** Set the target explode factor in [0..1]. Animation lerps over a few frames. */
  explode(factor: number): void;
  /** True when the active explode strategy carries the v2 blocker data that
   *  `revealNode` needs (i.e. the manifest had explosion-graph v2). When false,
   *  callers keep the plain highlight/isolate/frame select behaviour. */
  supportsReveal(): boolean;
  /** Targeted reveal (idiagram#6, the Partful "click a part, the covers slide
   *  off" UX): explode the MINIMAL set of parts occluding `id` from the current
   *  camera — the parts blocking it along the camera-facing axis, transitively —
   *  along that view axis, keep the target + movers bright while dimming the
   *  rest, and frame the target. Returns the ids that moved (empty when the part
   *  is already exposed from this view). A no-op returning [] when reveal is
   *  unsupported; check `supportsReveal()` first to decide the fallback. */
  revealNode(id: string): string[];
  /** Undo a reveal: ease all moved parts home, lift the dimming, and ease the
   *  camera back to the whole-assembly fit. Safe to call when nothing is revealed. */
  clearReveal(): void;
  /** Subscribe to click events. Callback receives the nearest non-synthetic
   *  node id (`id`) and the actual leaf node id at the click (`leafId`).
   *  Both null when the user clicks empty space.
   *  Regroup mode uses `leafId`; normal mode uses `id`. */
  onPick(cb: (id: string | null, leafId: string | null) => void): () => void;
  /** Subscribe to per-frame anchor updates: each "real" leaf-like node's
   *  centroid projected to canvas-pixel coordinates. Fires once per rendered
   *  frame after the scene has been drawn. Empty list until an assembly is
   *  loaded. */
  onAnchors(cb: (frame: AnchorFrame) => void): () => void;
  dispose(): void;
}

export interface ViewerControllerOptions {
  container: HTMLElement;
  background?: string;
  /** Strategy that decides per-part explode offsets. Defaults to
   *  RadialHierarchyExplode (v0 geometric baseline). Inject a manifest-graph
   *  strategy here (idiagram#3) to upgrade explode without renderer changes. */
  explodeStrategy?: ExplodeStrategy;
}

export async function createViewer(options: ViewerControllerOptions): Promise<ViewerController> {
  const { createThreeRenderer } = await import('./three-renderer.js');
  return createThreeRenderer(options);
}
